import React from "react";
import { useNavigate } from "react-router-dom";
import { EMOJI, plainText } from "./format";
import { MessageUnfurl, isMessageLink, parseMessageLink } from "@/components/MessageUnfurl";
import { analytics } from "./analytics";

export { plainText };

const INLINE_RE = /(```[\s\S]*?```)|(`[^`\n]+`)|(\*[^*\n]+\*)|(__[^_\n]+__)|(_[^_\n]+_)|(~[^~\n]+~)|(<(https?:\/\/[^|>\s]+)\|([^>]+)>)|(https?:\/\/[^\s<>]+)|((?<![\w@])@[A-Za-z0-9._-]+)|(:[a-z0-9_+-]+:)/g;

const internalPath = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith("/client/") ? u.pathname + u.search : null;
  } catch { return null; }
};

const Link = ({ href, children }) => {
  const navigate = useNavigate();
  const path = internalPath(href);
  if (!path) return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
  const msg = isMessageLink(href);
  return (
    <a href={href} className={msg ? "tc-msglink" : undefined} onClick={(e) => { e.preventDefault(); analytics.track("message_link_opened", { category: "navigation", label: msg ? "message" : "internal", target: "message", target_id: msg ? parseMessageLink(href)?.mid : undefined }); navigate(path); }} data-testid={msg ? "message-link" : "internal-link"}>
      {children}
    </a>
  );
};

function renderInline(text, ctx, keyPrefix = "i") {
  const out = [];
  let last = 0;
  let k = 0;
  const re = new RegExp(INLINE_RE.source, "g");
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyPrefix}${k++}`;
    const tok = m[0];
    if (m[1]) out.push(<pre className="tc-pre" key={key}>{tok.slice(3, -3).replace(/^\n/, "")}</pre>);
    else if (m[2]) out.push(<code className="tc-code" key={key}>{tok.slice(1, -1)}</code>);
    else if (m[3]) out.push(<strong key={key}>{renderInline(tok.slice(1, -1), ctx, key)}</strong>);
    else if (m[4]) out.push(<u key={key}>{renderInline(tok.slice(2, -2), ctx, key)}</u>);
    else if (m[5]) out.push(<em key={key}>{renderInline(tok.slice(1, -1), ctx, key)}</em>);
    else if (m[6]) out.push(<s key={key}>{renderInline(tok.slice(1, -1), ctx, key)}</s>);
    else if (m[7]) out.push(<Link key={key} href={m[8]}>{m[9]}</Link>);
    else if (m[10]) {
      const trail = tok.match(/[.,;:!?)]+$/)?.[0] || "";
      const url = trail ? tok.slice(0, -trail.length) : tok;
      out.push(<Link key={key} href={url}>{isMessageLink(url) ? "Message link" : url}</Link>);
      if (trail) out.push(trail);
    } else if (m[11]) {
      const handle = tok.slice(1).replace(/\.$/, "");
      const lower = handle.toLowerCase();
      const trailingDot = tok.endsWith(".") && handle !== tok.slice(1) ? "." : "";
      if (["channel", "here", "everyone"].includes(lower)) {
        out.push(<span key={key} className="tc-mention is-broadcast" data-testid={`mention-${lower}`}>@{lower}</span>);
      } else {
        const u = ctx.usersByHandle?.[lower];
        if (u) {
          const me = ctx.meId && u.id === ctx.meId;
          out.push(
            <span key={key} className={`tc-mention${me ? " is-me" : ""}`} data-testid={`mention-user-${u.handle}`} onClick={(e) => { e.stopPropagation(); ctx.onMentionClick?.(u); }}>
              @{u.display_name}
            </span>
          );
        } else out.push(tok);
      }
      if (trailingDot) out.push(trailingDot);
    } else if (m[12]) {
      const name = tok.slice(1, -1);
      const ch = EMOJI[name];
      out.push(ch ? <span key={key} className="tc-emoji">{ch}</span> : tok);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Mrkdwn({ text = "", ctx = {}, messageId, unfurl = true }) {
  if (!text) return null;
  const onlyEmoji = ctx.largeEmoji !== false && /^(\s*(:[a-z0-9_+-]+:|\p{Extended_Pictographic}\uFE0F?)\s*){1,3}$/u.test(text);
  const unfurls = [];
  if (unfurl) {
    for (const u of text.match(/https?:\/\/[^\s<>|]+/g) || []) {
      const p = isMessageLink(u) ? parseMessageLink(u) : null;
      if (p && !unfurls.some((x) => x.mid === p.mid) && p.mid !== messageId && unfurls.length < 3) unfurls.push(p);
    }
  }
  const blocks = [];
  const lines = text.split("\n");
  let i = 0;
  let b = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const buf = [line.replace(/^```/, "")];
      i++;
      while (i < lines.length && !lines[i].includes("```")) buf.push(lines[i++]);
      if (i < lines.length) buf.push(lines[i].replace(/```.*$/, ""));
      i++;
      blocks.push(<pre className="tc-pre" key={b++}>{buf.join("\n").replace(/^\n/, "").replace(/\n$/, "")}</pre>);
      continue;
    }
    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) buf.push(lines[i++].replace(/^>\s?/, ""));
      blocks.push(<blockquote className="tc-quote" key={b++}>{buf.map((l, j) => <p key={j}>{renderInline(l, ctx, `q${j}`)}</p>)}</blockquote>);
      continue;
    }
    if (/^\s*([-•*])\s+/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*([-•*])\s+/.test(lines[i])) buf.push(lines[i++].replace(/^\s*([-•*])\s+/, ""));
      blocks.push(<ul className="tc-list" style={{ listStyle: "disc" }} key={b++}>{buf.map((l, j) => <li key={j}>{renderInline(l, ctx, `ul${j}`)}</li>)}</ul>);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) buf.push(lines[i++].replace(/^\s*\d+\.\s+/, ""));
      blocks.push(<ol className="tc-list" style={{ listStyle: "decimal" }} key={b++}>{buf.map((l, j) => <li key={j}>{renderInline(l, ctx, `ol${j}`)}</li>)}</ol>);
      continue;
    }
    blocks.push(<p key={b++} className={onlyEmoji ? "tc-emoji is-big" : undefined}>{renderInline(line, ctx, `p${i}`)}</p>);
    i++;
  }
  return (
    <div className="tc-msg__text">
      {blocks}
      {unfurls.map((p) => <MessageUnfurl key={p.mid} cid={p.cid} mid={p.mid} sourceId={messageId} />)}
    </div>
  );
}
